/**
 * NBA API client — isolated. Hits /nba/* endpoints. Mirrors the CFB/NFL clients: a
 * failed fetch returns an HONEST EMPTY shape (never fabricated rows), flagged
 * `_sample:true`, so the UI can show its own empty / "coming soon" state.
 */
const BASE = import.meta.env.VITE_API_URL || 'https://linebrkr-deploy-production.up.railway.app'

// Same global access-code gate as MLB/NFL/CFB. Reuse the stored code so /nba board
// reads pass the gate; /nba/status and /nba/meta are public (no code needed).
const _hdrs = () => {
  try {
    const c = localStorage.getItem('brkr_access')
    return c ? { 'x-access-code': c } : {}
  } catch (_) { return {} }
}

async function tryGet(path, empty) {
  try {
    const r = await fetch(`${BASE}${path}`, { headers: _hdrs() })
    if (r.ok) return await r.json()
    return { ...(empty || {}), _sample: true, _status: r.status }
  } catch (_) {
    return { ...(empty || {}), _sample: true }
  }
}

export const getNBAStatus = (season = 2026) =>
  tryGet(`/nba/status?season=${season}`, { sport: 'nba', live: false, phase: 'coming_soon', counts: {} })

export const getNBAMeta = (season) =>
  tryGet(`/nba/meta${season ? `?season=${season}` : ''}`, { sport: 'nba', prop_types: [], positions: [] })

export const getNBALeaderboard = (season = 2026, date = '', propType = '', limit = 60, sort = 'edge') =>
  tryGet(`/nba/leaderboard?season=${season}&date=${date}&prop_type=${encodeURIComponent(propType)}&limit=${limit}&sort=${sort}`,
    { season, date, phase: 'coming_soon', plays: [] })

export const getNBAScorecard = (season = 2026) =>
  tryGet(`/nba/scorecard?season=${season}`, { season, overall: {}, by_prop: {}, by_confidence: {} })

export const getNBAResults = (season = 2026, date = '') =>
  tryGet(`/nba/results?season=${season}&date=${date}`, { season, date, plays: [] })

export const getNBAValidation = (seasons = '') =>
  tryGet(`/nba/validation${seasons ? `?seasons=${seasons}` : ''}`,
    { seasons: [], overall: {}, markets: {}, summary: { pass: [], review: [], building: [] } })

// One call for Home / Games / Injuries: games + lines + top plays per game, injury
// report, and the night's headline reads.
export const getNBASlate = (season = 2026, date = '', perGame = 4) =>
  tryGet(`/nba/slate?season=${season}&date=${date}&per_game=${perGame}`,
    { season, date, phase: 'coming_soon', counts: {}, games: [], top: [], value: [], strong: [],
      boosts: [], by_market: {}, injuries: [] })

// Player page: tonight's props + every book's price, game log, hit rates, splits.
export const getNBAPlayer = (bdlId, date = '', season = 2026) =>
  tryGet(`/nba/player?bdl_id=${encodeURIComponent(bdlId)}&date=${date || ''}&season=${season}`,
    { props: [], log: [], splits: null, minutes_trend: [] })

// Line movement since the first price seen, with toward/against the model's read.
export const getNBAMoves = (season = 2026, date = '', limit = 30) =>
  tryGet(`/nba/moves?season=${season}&date=${date}&limit=${limit}`, { moves: [], moved: 0, toward_model: 0 })

// ── NBA push alerts (same browser push subscription as NFL, registered under /nba) ──
export const nbaPushSupported = () =>
  typeof navigator !== 'undefined' && 'serviceWorker' in navigator &&
  typeof window !== 'undefined' && 'PushManager' in window && 'Notification' in window

const _b64 = (b64) => {
  const pad = '='.repeat((4 - (b64.length % 4)) % 4)
  const raw = atob((b64 + pad).replace(/-/g, '+').replace(/_/g, '/'))
  return Uint8Array.from(raw, c => c.charCodeAt(0))
}

export async function getNBAPushState() {
  if (!nbaPushSupported()) return { supported: false }
  const v = await tryGet('/nba/push/vapid', { enabled: false, public_key: '' })
  let sub = null
  try { sub = await (await navigator.serviceWorker.ready).pushManager.getSubscription() } catch (_) {}
  const st = sub ? await tryGet(`/nba/push/status?endpoint=${encodeURIComponent(sub.endpoint)}`, { subscribed: false }) : { subscribed: false }
  return { supported: true, enabled: !!v.enabled, publicKey: v.public_key || '',
           permission: Notification.permission, subscribed: !!st.subscribed, teams: st.teams || [] }
}

export async function subscribeNBAPush(teams = []) {
  if (!nbaPushSupported()) return { ok: false, reason: 'unsupported' }
  const v = await tryGet('/nba/push/vapid', { enabled: false })
  if (!v.enabled || !v.public_key) return { ok: false, reason: 'not_configured' }
  let perm = Notification.permission
  if (perm === 'default') perm = await Notification.requestPermission()
  if (perm !== 'granted') return { ok: false, reason: 'denied' }
  try {
    const reg = await navigator.serviceWorker.ready
    let sub = await reg.pushManager.getSubscription()
    if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: _b64(v.public_key) })
    const r = await fetch(`${BASE}/nba/push/subscribe`, {
      method: 'POST', headers: { 'content-type': 'application/json', ..._hdrs() },
      body: JSON.stringify({ ...sub.toJSON(), injuries: true, teams }),
    })
    return { ok: r.ok }
  } catch (_) { return { ok: false, reason: 'error' } }
}

export async function unsubscribeNBAPush() {
  try {
    const sub = await (await navigator.serviceWorker.ready).pushManager.getSubscription()
    // keep the browser subscription (NFL may use it) — just drop the NBA registration
    if (sub) await fetch(`${BASE}/nba/push/unsubscribe`, {
      method: 'POST', headers: { 'content-type': 'application/json', ..._hdrs() },
      body: JSON.stringify({ endpoint: sub.endpoint }) })
  } catch (_) {}
  return { ok: true }
}

// Check my slip — only the slip TEXT is sent (screenshots are read on the phone).
export async function checkNBASlip(text, season = 2026, date = '') {
  try {
    const r = await fetch(`${BASE}/nba/slip-check`, {
      method: 'POST', headers: { 'content-type': 'application/json', ..._hdrs() },
      body: JSON.stringify({ text, season, date }) })
    return r.ok ? await r.json() : { _error: true, legs: [] }
  } catch (_) { return { _error: true, legs: [] } }
}

export const askNBA = (q, season = 2026, date = '') =>
  tryGet(`/nba/ask?q=${encodeURIComponent(q)}&season=${season}&date=${date || ''}`, { answer: '', plays: [] })

// Where each defense is weakest (stat × position) + who attacks it tonight.
export const getNBASoftSpots = (season = 2026, date = '') =>
  tryGet(`/nba/soft-spots?season=${season}&date=${date || ''}`, { spots: [], teams: {} })
